import { Messages } from 'primereact/messages';
import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import LoadingPage from '../../../components/LoadingPage';
import { getErrorMessage } from '../../../utils/errors';
import { ErrorMessage } from '../../components/ErrorMessage';
import { getApiClient } from '../../../api';
import { showErrorMessage, showSuccessMessage } from '../../utils/messages';
import { TermAddForm } from './components/TermAddForm';
import { scrollTop } from '../../utils/scroll';
import { TermBreadCrumb } from './components/TermBreadCrumb';
import { formatDate } from '../../utils/format';

export default function TermAdd() {
    const { municipalityId, bodyType } = useParams();

    const navigate = useNavigate();

    const msgs = useRef(null);

    const [loading, setLoading] = useState(true);
    const [initError, setInitError] = useState(null);
    const [members, setMembers] = useState([]);
    const [politicalEntities, setPoliticalEntities] = useState([]);

    useEffect(() => {
        const fetchData = async () => {
            try {
                const [politicalEntitiesData, membersData] = await Promise.all([
                    getApiClient().getAdminPoliticalEntityByMunicipalityIdAndBody(municipalityId, bodyType),
                    getApiClient().getAdminMemberByMunicipalityIdAndBody(municipalityId, bodyType)
                ]);
                setPoliticalEntities(politicalEntitiesData);
                setMembers(membersData);
            } catch (error) {
                const errorMessage = getErrorMessage(error);
                setInitError(errorMessage ?? 'Nepodařilo se načíst data');
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, [municipalityId]);

    const onSubmit = async (data, leadership) => {
        const requestData = {
            year_from: data.year_from.getFullYear(),
            year_to: data.year_to.getFullYear(),
            order: data.order,
            coalition: data.coalition,
            members: data.members.map((member) => ({
                id: member.member_id,
                political_entity: member.political_entity
            })),
            leadership: leadership.flatMap(member =>
                member.leadership_roles.map(role => ({
                    id: member.id,
                    leader: role.leader,
                    role: role.role,
                    date_from: role.date_from ? formatDate(role.date_from) : null,
                    date_to: role.date_to ? formatDate(role.date_to) : null,
                }))
            )
        };

        try {
            const body = await getApiClient().postAdminTermByMunicipalityIdAndBody(municipalityId, bodyType, requestData);
            showSuccessMessage(msgs, `Funkční období č. ${body.order} bylo úspěšně přidáno`, {
                replace: true
            });
            navigate(`/admin/municipality/${municipalityId}/${bodyType}/funkcni-obdobi/${body.order}/zasedani`);
        } catch (error) {
            const errorMessage = getErrorMessage(error);
            showErrorMessage(msgs, errorMessage ?? 'Nepodařilo se přidat funkční období', { replace: true });
            scrollTop();
        }
    };

    return loading ? (
        <LoadingPage />
    ) : (
        <>
            <TermBreadCrumb
                extraItems={[
                    {
                        label: 'Přidat',
                        url: `/admin/municipality/${municipalityId}/${bodyType}/funkcni-obdobi/new`
                    }
                ]}
            />
            <h1>Přidat funkční období</h1>

            {initError ? (
                <ErrorMessage prefix='Nelze použít formulář' text={initError} />
            ) : (
                <>
                    <Messages ref={msgs} className='page__messages' />

                    <TermAddForm
                        members={members}
                        politicalEntities={politicalEntities}
                        onSubmit={onSubmit}
                    />
                </>
            )}
        </>
    );
}
